"use client";

import { PayChannel } from "@features/api/memberApi";
import { Track } from "@/src/lib/tracking";
import { sendGtmOrApp } from "@utils/sendGtmOrApp";

/**
 * 购买漏斗的 GA4 事件**唯一出口**（`begin_checkout` / `purchase`）。
 *
 * 为什么要收成一个出口（2026-08-31 审计结论）：这两条事件此前散在各入口自己写 ——
 * `begin_checkout` 有两份几乎一样的拷贝（Stripe / IAP），链上支付那条**漏写**；
 * `purchase` 更糟，只有 `/member` 与 `/payment` 两个页面报，从 Brief upsell、Briefing 付费墙、
 * Onboarding 付费墙、AlphaPick 付费墙、Chat 会员卡发起的内购成功后**一条都不报** ——
 * GA4 里 `begin_checkout` 是全量而 `purchase` 只有一部分，转化率被系统性低估，
 * 而 Google Ads 的出价模型吃的就是这个 `purchase`。
 *
 * **通道**：按规范 §0，这两条在 GA4 例外清单里（投放转化口径），走 `sendGtmOrApp`
 * （App 内经 bridge 交原生 Firebase，web 走 GTM）。**这里不发 OpenReplay** ——
 * 补 `Track.event("purchase")` 属于新增事件，要走事件变更协议（生成器 + 场景说明 + 群里知会），
 * 与本次「补回丢失的 GA 上报」不是同一件事。
 *
 * @see docs/engineering/tracking/tracking-conventions.md §0
 */

/**
 * GA4 ecommerce 需要的商品最小形状。
 *
 * `price` 是 `string | number`：后端 `MemberProduct.price` 线上下发的是**字符串**（`"299.99"`），
 * 而 GA4 的 `value` / `items[].price` 必须是数值 —— 字符串进去，Google Ads 侧就拿不到金额做
 * 出价与 ROAS。转换统一在 [toGa4Amount] 做，调用方不要各自 `Number()`。
 */
export interface PurchaseGtmPlan {
  id?: string;
  name?: string;
  price?: number | string;
}

/** 目前所有套餐都以美元计价（后端不下发币种）。 */
const CURRENCY_USD = "USD";

/** 后端字符串价 → GA4 数值；拿不到有效正数返回 undefined（GA4 宁可缺字段，也不要 `NaN` / `"299.99"`）。 */
function toGa4Amount(price: number | string | undefined): number | undefined {
  const parsed = Number(price);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function buildEcommerceItems(
  plan: PurchaseGtmPlan,
  amount: number | undefined,
) {
  return [
    {
      item_id: plan.id,
      item_name: plan.name,
      price: amount,
      quantity: 1,
    },
  ];
}

/**
 * GA4 `begin_checkout` —— **进入结账流程时**发，与支付渠道无关
 * （IAP / Stripe / 链上都要发，否则漏斗起点缺一块）。
 */
export function sendBeginCheckoutGtm(
  plan: PurchaseGtmPlan,
  options?: {
    /** web 渠道（EDG-552）：BI 按渠道拆漏斗起点；IAP / 链上不传，保持原口径 */
    payChannel?: PayChannel;
  },
): void {
  const amount = toGa4Amount(plan.price);
  sendGtmOrApp({
    event: "begin_checkout",
    ...(options?.payChannel ? { pay_channel: options.payChannel } : {}),
    ecommerce: {
      value: amount,
      currency: CURRENCY_USD,
      items: buildEcommerceItems(plan, amount),
    },
  });
}

/** [reportPurchaseGtm] 的 `orderId`：链上支付用真实 txHash，另外两条渠道拿不到订单号，用固定标识区分来源。 */
export const PURCHASE_ORDER_ID = {
  /** App 内购（IAP）——商店订单号不回传 web，沿用历史固定值，勿改（GA4 报表按它筛来源）。 */
  APP: "app_purchase",
  /** Stripe web 回跳 —— 同上。 */
  STRIPE_WEB: "stripe_web_purchase",
  /** PayPal web 回跳（EDG-539）—— 与 Stripe 区分来源口径，BI 侧按渠道看转化。 */
  PAYPAL_WEB: "paypal_web_purchase",
  /** Airwallex web 回跳主动确认成功（EDG-552）。 */
  AIRWALLEX_WEB: "airwallex_web_purchase",
} as const;

const WEB_ORDER_ID_BY_CHANNEL: Partial<Record<PayChannel, string>> = {
  [PayChannel.PAYPAL]: PURCHASE_ORDER_ID.PAYPAL_WEB,
  [PayChannel.AIRWALLEX]: PURCHASE_ORDER_ID.AIRWALLEX_WEB,
};

/**
 * 回跳 query 的 `channel` → GA4 `transaction_id`。
 *
 * 缺省 / 未登记（含 STRIPE 与改造前的旧回跳链接）落回 `STRIPE_WEB` —— 与改造前「非 PAYPAL 一律
 * STRIPE_WEB」的口径一致，不让历史报表跳变。
 */
export function orderIdByChannel(channel?: string | null): string {
  return (
    WEB_ORDER_ID_BY_CHANNEL[channel as PayChannel] ??
    PURCHASE_ORDER_ID.STRIPE_WEB
  );
}

/**
 * GA4 `purchase` —— **购买成功时**发。
 *
 * 金额或 orderId 缺失时**不发**（宁可少一条，也不要往 GA4 灌 `value: 0` 的假转化），
 * 但会留一条日志说明缺了什么，否则线上只会表现为"这个入口的转化凭空少了"。
 */
export function reportPurchaseGtm(params: {
  orderId: string;
  /** `null` 是产品列表没命中的常见形态（`find(...) || null`），与 `undefined` 同等处理。 */
  plan: PurchaseGtmPlan | null | undefined;
}): void {
  const { orderId, plan } = params;
  const amount = toGa4Amount(plan?.price);

  if (!amount || !orderId || !plan) {
    Track.log("info", "purchaseGtm", "purchase 未上报，前置信息不全", {
      orderId,
      plan: String(JSON.stringify(plan)),
    });
    return;
  }

  const eventData = {
    event: "purchase",
    ecommerce: {
      transaction_id: orderId,
      value: amount,
      tax: 0,
      shipping: 0,
      currency: CURRENCY_USD,
      items: buildEcommerceItems(plan, amount),
    },
  };
  sendGtmOrApp(eventData);
  Track.log("info", "purchaseGtm", "purchase 已上报", {
    cause: String(JSON.stringify(eventData)),
  });
}
